import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Linkedin, Plus, ShieldCheck, ShieldAlert, RefreshCw, Trash2, Activity,
  Send, UserPlus, CheckCircle2, XCircle, Clock, ExternalLink, FileText, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { relativeTime } from "@/lib/utils";

const STATUS_BADGE = {
  connected: "text-emerald-500 border-emerald-500/40 bg-emerald-500/10",
  pending: "text-amber-accent border-amber-accent/40 bg-amber-accent/10",
  needs_reconnect: "text-destructive border-destructive/40 bg-destructive/10",
  disconnected: "text-destructive border-destructive/40 bg-destructive/10",
  auth_failed: "text-destructive border-destructive/40 bg-destructive/10",
  stopped: "text-muted-foreground border-border bg-muted/30",
};

const ACTION_LABEL = {
  send_connection_request: "Connection requests",
  send_message: "Direct messages",
  create_post: "Posts",
  comment_on_post: "Comments",
};

const ACTION_TYPES = [
  { key: "send_connection_request", label: "Connection request", icon: UserPlus },
  { key: "send_message", label: "Direct message", icon: Send },
  { key: "create_post", label: "Post (self / company)", icon: FileText },
  { key: "comment_on_post", label: "Comment", icon: MessageCircle },
];

export default function LinkedInBrowserPage() {
  const [accounts, setAccounts] = useState([]);
  const [unipileConfigured, setUnipileConfigured] = useState(true);
  const [active, setActive] = useState(null);
  const [usage, setUsage] = useState(null);
  const [actionLog, setActionLog] = useState([]);
  const [connectOpen, setConnectOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [discoverable, setDiscoverable] = useState([]);
  const [syncing, setSyncing] = useState(null);

  // Action composer state
  const [actionType, setActionType] = useState("send_connection_request");
  const [providerId, setProviderId] = useState("");
  const [actionText, setActionText] = useState("");
  const [postOrgUrn, setPostOrgUrn] = useState("");
  const [commentSocialId, setCommentSocialId] = useState("");
  const [sendingAction, setSendingAction] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/linkedin-browser/accounts");
      setAccounts(data.accounts || []);
      setUnipileConfigured(Boolean(data.unipile_configured));
      if (data.accounts?.length && !active) setActive(data.accounts[0].id);
    } catch { toast.error("Failed to load accounts"); }
  }, [active]);

  useEffect(() => { load(); }, [load]);

  // Poll while a pending Hosted Auth is in progress — webhook lands async
  useEffect(() => {
    const hasPending = accounts.some((a) => a.status === "pending");
    if (!hasPending) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [accounts, load]);

  useEffect(() => {
    if (!active) return;
    (async () => {
      try {
        const [u, l] = await Promise.all([
          api.get(`/linkedin-browser/accounts/${active}/usage`),
          api.get(`/linkedin-browser/accounts/${active}/log?limit=20`),
        ]);
        setUsage(u.data);
        setActionLog(l.data.actions || []);
      } catch { /* ignore */ }
    })();
  }, [active]);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/linkedin-browser/accounts", {
        label: label.trim() || "LinkedIn account",
      });
      if (data?.auth_url) {
        toast.success("Redirecting to Unipile Hosted Auth…");
        // Open in new tab so the polling page stays alive
        window.open(data.auth_url, "_blank", "noopener,noreferrer");
        setConnectOpen(false); setLabel("");
        await load();
      } else {
        toast.error("No auth URL returned");
      }
    } catch (e) {
      toast.error(`Connection failed: ${e?.response?.data?.detail || "unknown"}`);
    }
    setConnecting(false);
  };

  const openConnect = async () => {
    setConnectOpen(true);
    setDiscoverable([]);
    if (!unipileConfigured) return;
    try {
      const { data } = await api.get("/linkedin-browser/unipile-discover");
      setDiscoverable(data.available || []);
    } catch { /* silent */ }
  };

  const syncOne = async (account_id) => {
    setSyncing(account_id);
    try {
      const { data } = await api.post("/linkedin-browser/accounts/sync", { account_id });
      if (data.ok) {
        toast.success(`✓ ${data.display_name || "Account"} imported (${data.organizations_count} company pages)`);
        setConnectOpen(false);
        await load();
        setActive(data.account_db_id);
      } else {
        toast.error(`Sync failed: ${data.error}`);
      }
    } catch (e) {
      toast.error(`Sync failed: ${e?.response?.data?.detail || "unknown"}`);
    }
    setSyncing(null);
  };

  const verify = async (id) => {
    setVerifying(true);
    try {
      const { data } = await api.post(`/linkedin-browser/accounts/${id}/verify`);
      if (data.ok) toast.success("Session is live ✓");
      else toast.error(`Verification failed: ${data.error || "unknown"}`);
      await load();
    } catch { toast.error("Verify failed"); }
    setVerifying(false);
  };

  const reconnect = async (id) => {
    try {
      const { data } = await api.post(`/linkedin-browser/accounts/${id}/reconnect`);
      if (data?.auth_url) {
        window.open(data.auth_url, "_blank", "noopener,noreferrer");
        toast.success("Reconnect link opened — finish on Unipile");
        await load();
      }
    } catch (e) {
      toast.error(`Reconnect failed: ${e?.response?.data?.detail || "unknown"}`);
    }
  };

  const remove = async (id) => {
    if (!confirm("Remove this LinkedIn connection? It will be detached from Unipile.")) return;
    try {
      await api.delete(`/linkedin-browser/accounts/${id}`);
      toast.success("Account removed");
      setActive(null);
      await load();
    } catch { toast.error("Delete failed"); }
  };

  const runAction = async () => {
    if (!active) return;
    setSendingAction(true);
    try {
      let endpoint = "";
      let body = {};
      if (actionType === "send_connection_request") {
        if (!providerId.trim()) { toast.error("LinkedIn provider ID required"); setSendingAction(false); return; }
        endpoint = `/linkedin-browser/accounts/${active}/send-connection`;
        body = { provider_id: providerId, note: actionText || null };
      } else if (actionType === "send_message") {
        if (!providerId.trim()) { toast.error("LinkedIn provider ID required"); setSendingAction(false); return; }
        endpoint = `/linkedin-browser/accounts/${active}/send-message`;
        body = { provider_id: providerId, text: actionText };
      } else if (actionType === "create_post") {
        if (!actionText.trim()) { toast.error("Post text required"); setSendingAction(false); return; }
        endpoint = `/linkedin-browser/accounts/${active}/post`;
        body = { text: actionText, organization_urn: postOrgUrn.trim() || null };
      } else if (actionType === "comment_on_post") {
        if (!commentSocialId.trim()) { toast.error("Post social_id (urn:li:activity:...) required"); setSendingAction(false); return; }
        endpoint = `/linkedin-browser/accounts/${active}/comment`;
        body = { social_id: commentSocialId.trim(), text: actionText, organization_urn: postOrgUrn.trim() || null };
      }
      await api.post(endpoint, body);
      toast.success(`✓ ${ACTION_LABEL[actionType]} sent`);
      setProviderId(""); setActionText(""); setCommentSocialId("");
      const [u, l] = await Promise.all([
        api.get(`/linkedin-browser/accounts/${active}/usage`),
        api.get(`/linkedin-browser/accounts/${active}/log?limit=20`),
      ]);
      setUsage(u.data);
      setActionLog(l.data.actions || []);
    } catch (e) {
      toast.error(`Action failed: ${e?.response?.data?.detail || "unknown"}`);
    }
    setSendingAction(false);
  };

  const activeAccount = accounts.find((a) => a.id === active);

  return (
    <DashboardLayout
      eyebrow="LinkedIn Automation"
      title="LinkedIn via Unipile API"
      actions={
        <Button
          onClick={openConnect}
          data-testid="li-connect-btn"
          className="rounded-sm"
          disabled={!unipileConfigured}
        >
          <Plus className="w-4 h-4 mr-1.5" /> Connect LinkedIn
        </Button>
      }
    >
      {!unipileConfigured && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-sm p-3 mb-6 flex gap-3" data-testid="li-not-configured">
          <ShieldAlert className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
          <div className="text-xs text-foreground/80 leading-relaxed">
            <strong className="text-destructive">Unipile is not configured.</strong>{" "}
            Add <code className="font-mono">UNIPILE_DSN</code> and <code className="font-mono">UNIPILE_API_KEY</code> to backend
            <code className="font-mono"> .env</code> (get them from your Unipile dashboard → API Keys + DSN).
            Until then, LinkedIn actions are disabled.
          </div>
        </div>
      )}

      {/* Info callout */}
      <div className="bg-signal-500/5 border border-signal-500/20 rounded-sm p-3 mb-6 flex gap-3" data-testid="li-info-callout">
        <Linkedin className="w-4 h-4 text-signal-500 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-foreground/80 leading-relaxed">
          <strong className="text-signal-500">Connection happens on Unipile&apos;s hosted page.</strong>{" "}
          Click Connect → sign into LinkedIn once → return here. Unipile keeps the session alive 24/7 — no PIN challenges, no datacenter IP bans.
          You can post as a personal profile or as a company page (set <code className="font-mono">organization_urn</code>).
        </div>
      </div>

      {accounts.length === 0 ? (
        <EmptyState onConnect={openConnect} disabled={!unipileConfigured} />
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Account list */}
          <div className="space-y-2" data-testid="li-account-list">
            {accounts.map((a) => (
              <button
                key={a.id}
                onClick={() => setActive(a.id)}
                className={`w-full text-left data-card transition-colors ${active === a.id ? "border-signal-500/60" : "hover:border-foreground/30"}`}
                data-testid={`li-account-${a.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-sm truncate inline-flex items-center gap-2">
                      <Linkedin className="w-3.5 h-3.5 text-signal-500" /> {a.display_name || a.label}
                    </div>
                    <div className="text-xs text-muted-foreground">{a.label}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-sm text-[0.6rem] tracking-widest uppercase font-mono border ${STATUS_BADGE[a.status] || STATUS_BADGE.pending}`}>
                    {a.status}
                  </span>
                </div>
                <div className="text-[0.65rem] text-muted-foreground">
                  Added {relativeTime(a.created_at)}
                  {a.last_verified && ` · Verified ${relativeTime(a.last_verified)}`}
                </div>
              </button>
            ))}
          </div>

          {/* Active account detail */}
          <div className="lg:col-span-2 space-y-4">
            {activeAccount && (
              <>
                <div className="data-card">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                    <div>
                      <div className="font-display font-semibold text-base">{activeAccount.display_name || activeAccount.label}</div>
                      <div className="text-xs text-muted-foreground">{activeAccount.label}</div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => verify(activeAccount.id)} disabled={verifying} data-testid="li-verify-btn">
                        <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${verifying ? "animate-spin" : ""}`} />
                        Verify
                      </Button>
                      {(activeAccount.status === "needs_reconnect" || activeAccount.status === "disconnected" || activeAccount.status === "auth_failed") && (
                        <Button size="sm" variant="outline" onClick={() => reconnect(activeAccount.id)} data-testid="li-reconnect-btn">
                          <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Reconnect
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => remove(activeAccount.id)} className="text-destructive" data-testid="li-delete-btn">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>

                  {usage && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="li-usage">
                      {Object.entries(usage.usage || {}).map(([k, u]) => {
                        const pct = u.cap > 0 ? Math.min(100, (u.used / u.cap) * 100) : 0;
                        return (
                          <div key={k} className="bg-background/40 border border-border rounded-sm p-2.5">
                            <div className="text-[0.6rem] tracking-widest uppercase text-muted-foreground">{ACTION_LABEL[k] || k}</div>
                            <div className="font-mono text-sm mt-0.5">{u.used} / {u.cap}</div>
                            <div className="w-full h-1 bg-muted rounded mt-1.5 overflow-hidden">
                              <div className={`h-full ${pct >= 80 ? "bg-destructive" : pct >= 50 ? "bg-amber-accent" : "bg-signal-500"}`} style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {activeAccount.status === "pending" && (
                  <div className="data-card border-amber-accent/40 flex items-start gap-3" data-testid="li-pending-state">
                    <Clock className="w-4 h-4 text-amber-accent flex-shrink-0 mt-1 animate-pulse" />
                    <div>
                      <div className="label-eyebrow text-amber-accent">Waiting for Hosted Auth</div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Finish the LinkedIn sign-in on the Unipile tab. This page polls every 5s — status will flip to <code className="font-mono">connected</code> automatically.
                      </p>
                    </div>
                  </div>
                )}

                {activeAccount.status === "connected" && (
                  <div className="data-card space-y-3" data-testid="li-action-composer">
                    <div className="label-eyebrow inline-flex items-center gap-2">
                      <Send className="w-3.5 h-3.5" /> Send action
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {ACTION_TYPES.map(({ key, label: l, icon: Icon }) => (
                        <button
                          key={key}
                          type="button"
                          data-testid={`li-action-type-${key}`}
                          onClick={() => setActionType(key)}
                          className={`px-3 py-1.5 rounded-sm text-xs border ${actionType === key ? "bg-signal-500/10 border-signal-500/40 text-signal-500" : "border-border text-muted-foreground hover:text-foreground"}`}
                        >
                          <Icon className="w-3.5 h-3.5 inline mr-1.5" /> {l}
                        </button>
                      ))}
                    </div>

                    {(actionType === "send_connection_request" || actionType === "send_message") && (
                      <div>
                        <Label className="text-xs mb-1 block">LinkedIn provider ID (member URN)</Label>
                        <Input
                          data-testid="li-provider-id"
                          value={providerId}
                          onChange={(e) => setProviderId(e.target.value)}
                          placeholder="ACoAAAA... (LinkedIn member ID)"
                          className="font-mono text-xs"
                        />
                        <p className="text-[0.65rem] text-muted-foreground mt-1">
                          Get it from Unipile&apos;s /users endpoint or a profile lookup. We&apos;ll auto-fill this from Prospect lookups in a future iteration.
                        </p>
                      </div>
                    )}

                    {actionType === "comment_on_post" && (
                      <div>
                        <Label className="text-xs mb-1 block">Post social_id (urn:li:activity:...)</Label>
                        <Input
                          data-testid="li-social-id"
                          value={commentSocialId}
                          onChange={(e) => setCommentSocialId(e.target.value)}
                          placeholder="urn:li:activity:7332661864792854528"
                          className="font-mono text-xs"
                        />
                      </div>
                    )}

                    {(actionType === "create_post" || actionType === "comment_on_post") && (
                      <div>
                        <Label className="text-xs mb-1 block">
                          Post as
                        </Label>
                        <select
                          data-testid="li-org-urn"
                          value={postOrgUrn}
                          onChange={(e) => setPostOrgUrn(e.target.value)}
                          className="w-full px-3 py-2 text-sm bg-background border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-signal-500 font-mono"
                        >
                          <option value="">👤 Your personal profile</option>
                          {(activeAccount.organizations || []).map((o) => (
                            <option key={o.organization_urn} value={o.organization_urn} disabled={!o.messaging_enabled && actionType !== "create_post"}>
                              🏢 {o.name}{!o.messaging_enabled ? " (page admin only)" : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div>
                      <Label className="text-xs mb-1 block">
                        {actionType === "send_connection_request" ? "Note (optional, 300 chars max)"
                          : actionType === "send_message" ? "Message text"
                          : actionType === "create_post" ? "Post body"
                          : "Comment text"}
                      </Label>
                      <textarea
                        data-testid="li-action-text"
                        value={actionText}
                        onChange={(e) => setActionText(e.target.value)}
                        rows={actionType === "send_connection_request" ? 3 : 6}
                        maxLength={actionType === "send_connection_request" ? 300 : actionType === "comment_on_post" ? 1250 : 3000}
                        placeholder={
                          actionType === "send_connection_request" ? "Hi {name} — saw your work on {topic}, would love to connect."
                          : actionType === "send_message" ? "Write your message..."
                          : actionType === "create_post" ? "What do you want to share?"
                          : "Add a comment..."
                        }
                        className="w-full px-3 py-2 text-sm bg-background border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-signal-500"
                      />
                    </div>
                    <Button onClick={runAction} disabled={sendingAction} data-testid="li-run-action-btn" className="rounded-sm">
                      <Send className="w-3.5 h-3.5 mr-1.5" />
                      {sendingAction ? "Sending via Unipile…" : `Execute ${ACTION_LABEL[actionType]?.toLowerCase()}`}
                    </Button>
                  </div>
                )}

                <div className="data-card" data-testid="li-action-log">
                  <div className="label-eyebrow mb-2 inline-flex items-center gap-2">
                    <Activity className="w-3.5 h-3.5" /> Recent actions
                  </div>
                  {actionLog.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No actions yet.</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {actionLog.map((a) => (
                        <li key={a.id} className="flex items-start gap-2 text-xs">
                          {a.status === "ok" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-destructive mt-0.5 flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <div>
                              <span className="font-medium">{ACTION_LABEL[a.action_type] || a.action_type}</span>
                              {a.target && (
                                <span className="text-signal-500 inline-flex items-center gap-1 ml-2 text-[0.65rem] font-mono">
                                  {String(a.target).slice(0, 60)}
                                </span>
                              )}
                            </div>
                            {a.error && <div className="text-destructive text-[0.65rem]">{a.error}</div>}
                          </div>
                          <span className="text-[0.6rem] text-muted-foreground whitespace-nowrap inline-flex items-center gap-1">
                            <Clock className="w-2.5 h-2.5" /> {relativeTime(a.created_at)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {connectOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60 backdrop-blur-sm p-0 md:p-4"
          onClick={() => !connecting && setConnectOpen(false)}
          data-testid="li-connect-modal"
        >
          <div
            className="bg-card border border-border rounded-t-md md:rounded-sm w-full max-w-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <div className="font-display font-semibold inline-flex items-center gap-2">
                <Linkedin className="w-4 h-4 text-signal-500" /> Connect LinkedIn via Unipile
              </div>
            </div>
            <div className="p-5 space-y-4">
              {discoverable.length > 0 && (
                <div className="space-y-2" data-testid="li-discover-section">
                  <div className="label-eyebrow inline-flex items-center gap-2">
                    <ShieldCheck className="w-3.5 h-3.5" /> Existing Unipile accounts ({discoverable.length})
                  </div>
                  <p className="text-xs text-muted-foreground">
                    These LinkedIn accounts are already connected on Unipile&apos;s side. Import to use them here.
                  </p>
                  <ul className="space-y-1.5">
                    {discoverable.map((acc) => (
                      <li key={acc.account_id} className="data-card !p-3 flex items-center gap-3" data-testid={`li-discover-${acc.account_id}`}>
                        <Linkedin className="w-4 h-4 text-signal-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">
                            {acc.display_name}
                            {acc.premium && <span className="ml-2 text-[0.6rem] font-mono uppercase tracking-widest text-amber-accent border border-amber-accent/40 bg-amber-accent/10 px-1.5 py-0.5 rounded-sm">Premium</span>}
                          </div>
                          <div className="text-[0.65rem] text-muted-foreground font-mono truncate">
                            {acc.public_identifier} · {acc.organizations.length} page{acc.organizations.length === 1 ? "" : "s"} · {acc.status}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => syncOne(acc.account_id)}
                          disabled={syncing === acc.account_id || acc.bound_elsewhere}
                          data-testid={`li-sync-btn-${acc.account_id}`}
                          className="rounded-sm flex-shrink-0"
                        >
                          {syncing === acc.account_id ? "Importing..." : acc.bound_elsewhere ? "In other workspace" : "Import"}
                        </Button>
                      </li>
                    ))}
                  </ul>
                  <div className="border-t border-border pt-3 mt-3">
                    <div className="label-eyebrow">Or — connect a new account</div>
                  </div>
                </div>
              )}
              <div className="bg-muted/30 border border-border rounded-sm p-3 text-xs space-y-2">
                <div className="font-semibold text-foreground">How this works:</div>
                <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                  <li>Click <strong>Open Unipile auth</strong> → opens a Unipile Hosted Auth tab.</li>
                  <li>Sign in to LinkedIn there once. Unipile stores the session securely on their infrastructure.</li>
                  <li>You&apos;ll be redirected back; this page polls and flips to <code className="font-mono">connected</code>.</li>
                  <li>No PINs, no datacenter IP bans, no cookies leaving our control.</li>
                </ol>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Label (optional)</Label>
                <Input
                  data-testid="li-label-input"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. Europacific outreach burner — Rok Kobal"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => !connecting && setConnectOpen(false)} disabled={connecting}>Cancel</Button>
                <Button onClick={connect} disabled={connecting} data-testid="li-submit-btn">
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                  {connecting ? "Generating auth link..." : "Open Unipile auth"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function EmptyState({ onConnect, disabled }) {
  return (
    <div className="data-card text-center py-16" data-testid="li-empty-state">
      <Linkedin className="w-10 h-10 text-signal-500 mx-auto mb-3" />
      <h3 className="font-display text-lg font-semibold mb-1">Connect your first LinkedIn account</h3>
      <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
        We use Unipile for LinkedIn — connect once via their hosted auth, then post, comment, message and send invitations from here.
      </p>
      <Button onClick={onConnect} disabled={disabled} data-testid="li-empty-connect-btn" className="rounded-sm">
        <Plus className="w-4 h-4 mr-1.5" /> Connect LinkedIn
      </Button>
    </div>
  );
}
